import { ArtworkPayload } from "./artworks.types";
import axios from "axios";
import { axiosInstance } from "../../utils/api";
import { Artwork } from "@/types/artworksData";
import {
  ArtworkWorkspaceBatchJob,
  ArtworkWorkspaceBatchResultsPage,
  ArtworkWorkspaceBatchUploadInstruction,
  ArtworkWorkspaceBatchUploadSession,
} from "@/types/artworkWorkspace";
import {
  ArtworkOptimizationConfig,
  ArtworkOptimizationJob,
  ArtworkOptimizationSelection,
  CreateArtworkOptimizationJobRequest,
  CreateArtworkOptimizationJobResponse,
  RetryArtworkOptimizationUploadRequest,
  RetryArtworkOptimizationUploadResponse,
  DISABLED_ARTWORK_OPTIMIZATION_CONFIG,
  PendingArtworkOptimizationJobsResponse,
} from "../../types/artworkOptimization";
interface ApiResponse<T> {
  status: boolean;
  data: T;
  msg?: string;
}

const directUploadClient = axios.create({ withCredentials: false });
delete directUploadClient.defaults.headers.common.Authorization;
delete directUploadClient.defaults.headers.common.authorization;

const getAllowedSignedUploadHeaders = (headers: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(headers).filter(([name]) => {
      const normalized = name.toLowerCase();
      return (
        normalized === "content-type" ||
        normalized === "content-md5" ||
        normalized.startsWith("x-amz-")
      );
    })
  );

const appendJsonIfPresent = (formData: FormData, key: string, value: unknown) => {
  if (!value || typeof value !== "object") {
    return;
  }

  if (Object.keys(value as Record<string, unknown>).length === 0) {
    return;
  }

  formData.append(key, JSON.stringify(value));
};

const buildEditableEdmProfilePatch = (edmProfile?: Record<string, any>) => {
  if (!edmProfile || typeof edmProfile !== "object") {
    return undefined;
  }

  const allowedKeys = [
    "dataProvider",
    "country",
    "subNationalAuthority",
    "titles",
    "descriptions",
    "subjects",
    "types",
  ];
  return allowedKeys.reduce<Record<string, any>>((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(edmProfile, key)) {
      acc[key] = edmProfile[key];
    }
    return acc;
  }, {});
};

export const createBatchArtworks = async (metadata: File, dataFiles: File[]) => {
  const formData = new FormData();

  formData.append('metadata', metadata);
  for (const dataFile of dataFiles) {
    formData.append('dataFiles', dataFile);
  }

  const response = await axiosInstance.post<ApiResponse<ArtworkPayload>>(`/artworks/batch`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  
  return response.data;  
}

export type CreateArtworkWorkspaceBatchJobOptions = {
  targetUserId?: string;
  defaultArtistName?: string;
  spatialMigrationRunId?: string;
  spatialMatches?: Array<{
    sourceObjectId: string;
    uploadId: string;
    workbookRowIndex?: number;
  }>;
  onUploadProgress?: (percent: number) => void;
  signal?: AbortSignal;
};

const WORKSPACE_BATCH_UPLOAD_CONCURRENCY = 3;

const uploadWorkspaceBatchFile = async (
  instruction: ArtworkWorkspaceBatchUploadInstruction,
  file: File,
  onProgress: (loadedBytes: number) => void,
  signal?: AbortSignal,
) => {
  await directUploadClient.put(instruction.url, file, {
    headers: getAllowedSignedUploadHeaders(instruction.headers),
    signal,
    withCredentials: false,
    onUploadProgress: (event) => onProgress(Math.min(event.loaded, file.size)),
  });
};

export const uploadWorkspaceBatchFiles = async (
  session: ArtworkWorkspaceBatchUploadSession,
  dataFiles: File[],
  options: Pick<CreateArtworkWorkspaceBatchJobOptions, 'onUploadProgress' | 'signal'> = {},
): Promise<void> => {
  const filesByName = new Map(dataFiles.map((file) => [file.name, file]));
  const loadedByUploadId = new Map<string, number>();
  const totalBytes = session.uploads.reduce((total, upload) => total + upload.sizeBytes, 0);
  let cursor = 0;
  const reportProgress = (uploadId: string, loadedBytes: number) => {
    loadedByUploadId.set(uploadId, loadedBytes);
    const loaded = Array.from(loadedByUploadId.values()).reduce((total, value) => total + value, 0);
    options.onUploadProgress?.(totalBytes > 0 ? Math.min(100, Math.round((loaded / totalBytes) * 100)) : 100);
  };

  await Promise.all(Array.from(
    { length: Math.min(WORKSPACE_BATCH_UPLOAD_CONCURRENCY, session.uploads.length) },
    async () => {
      while (cursor < session.uploads.length) {
        const index = cursor;
        cursor += 1;
        let instruction = session.uploads[index];
        const file = filesByName.get(instruction.fileName);
        if (!file || file.size !== instruction.sizeBytes) {
          throw new Error(`Batch upload file changed after validation: ${instruction.fileName}`);
        }
        try {
          await uploadWorkspaceBatchFile(
            instruction,
            file,
            (loaded) => reportProgress(instruction.uploadId, loaded),
            options.signal,
          );
        } catch (error) {
          if (options.signal?.aborted) throw error;
          reportProgress(instruction.uploadId, 0);
          const refreshed = await axiosInstance.post(
            `/artworks/workspace/batch/upload-sessions/${session.sessionId}/upload-instructions`,
            { uploadIds: [instruction.uploadId] },
            { signal: options.signal },
          );
          instruction = (refreshed.data.data as ArtworkWorkspaceBatchUploadSession).uploads[0];
          await uploadWorkspaceBatchFile(
            instruction,
            file,
            (loaded) => reportProgress(instruction.uploadId, loaded),
            options.signal,
          );
        }
      }
    },
  ));
  options.onUploadProgress?.(100);
};

export const createArtworkWorkspaceBatchJob = async (
  metadata: File,
  dataFiles: File[],
  options: CreateArtworkWorkspaceBatchJobOptions = {}
): Promise<ArtworkWorkspaceBatchJob> => {
  const formData = new FormData();
  formData.append('metadata', metadata);
  formData.append('files', JSON.stringify(dataFiles.map((dataFile) => ({
    fileName: dataFile.name,
    sizeBytes: dataFile.size,
  }))));
  if (options.targetUserId) {
    formData.append('targetUserId', options.targetUserId);
  }
  if (options.defaultArtistName?.trim()) {
    formData.append('defaultArtistName', options.defaultArtistName.trim());
  }
  if (options.spatialMatches?.length) {
    formData.append('spatialMatches', JSON.stringify(options.spatialMatches));
  }

  const endpoint = options.spatialMigrationRunId
    ? `/admin/spatial-migration-runs/${options.spatialMigrationRunId}/batch-upload-sessions`
    : `/artworks/workspace/batch/upload-sessions`;
  const response = await axiosInstance.post(endpoint, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    signal: options.signal,
  });
  const session = response.data.data as ArtworkWorkspaceBatchUploadSession;
  await uploadWorkspaceBatchFiles(session, dataFiles, options);
  const completed = await axiosInstance.post(
    `/artworks/workspace/batch/upload-sessions/${session.sessionId}/complete`,
    {},
    { signal: options.signal },
  );
  return completed.data.data as ArtworkWorkspaceBatchJob;
}

export const getArtworkWorkspaceBatchJob = async (jobId: string): Promise<ArtworkWorkspaceBatchJob> => {
  const response = await axiosInstance.get(`/artworks/workspace/batch/jobs/${jobId}`);
  return response.data.data as ArtworkWorkspaceBatchJob;
}

export const getArtworkWorkspaceBatchJobResults = async (jobId: string, page = 1, limit = 25): Promise<ArtworkWorkspaceBatchResultsPage> => {
  const response = await axiosInstance.get(`/artworks/workspace/batch/jobs/${jobId}/results`, {
    params: { page, limit },
  });
  return response.data.data as ArtworkWorkspaceBatchResultsPage;
}

export const retryArtworkWorkspaceBatchJobFailures = async (jobId: string): Promise<ArtworkWorkspaceBatchJob> => {
  const response = await axiosInstance.post(`/artworks/workspace/batch/jobs/${jobId}/retry-failures`, {});
  return response.data.data as ArtworkWorkspaceBatchJob;
}

export const getArtworkOptimizationConfig = async (): Promise<ArtworkOptimizationConfig> => {
  const response = await axiosInstance.get("/artworks/workspace/config");
  return response.data?.data?.optimization || DISABLED_ARTWORK_OPTIMIZATION_CONFIG;
};

export const createArtworkOptimizationJob = async (
  request: CreateArtworkOptimizationJobRequest
): Promise<CreateArtworkOptimizationJobResponse> => {
  const response = await axiosInstance.post("/artworks/workspace/optimization-jobs", request);
  return response.data.data as CreateArtworkOptimizationJobResponse;
};

export const getPendingArtworkOptimizationJobs = async (): Promise<PendingArtworkOptimizationJobsResponse> => {
  const response = await axiosInstance.get("/artworks/workspace/optimization-jobs");
  return response.data.data as PendingArtworkOptimizationJobsResponse;
};
export interface ArtworkOptimizationUploadOptions {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

export const uploadArtworkOptimizationSource = async (
  instruction: CreateArtworkOptimizationJobResponse["upload"],
  file: File,
  options: ArtworkOptimizationUploadOptions = {}
): Promise<void> => {
  await directUploadClient.put(instruction.url, file, {
    headers: getAllowedSignedUploadHeaders(instruction.headers),
    signal: options.signal,
    withCredentials: false,
    onUploadProgress: (event) => {
      const total = event.total || file.size;
      if (total > 0) {
        options.onProgress?.(Math.min(100, Math.round((event.loaded / total) * 100)));
      }
    },
  });
};

export const markArtworkOptimizationUploadComplete = async (
  jobId: string
): Promise<ArtworkOptimizationJob> => {
  const response = await axiosInstance.post(
    `/artworks/workspace/optimization-jobs/${jobId}/upload-complete`,
    {}
  );
  return response.data.data.job as ArtworkOptimizationJob;
};

export const retryArtworkOptimizationUpload = async (
  jobId: string,
  input: RetryArtworkOptimizationUploadRequest
): Promise<RetryArtworkOptimizationUploadResponse> => {
  const response = await axiosInstance.post(
    `/artworks/workspace/optimization-jobs/${jobId}/retry-upload`,
    input
  );
  return response.data.data as RetryArtworkOptimizationUploadResponse;
};

export const getArtworkOptimizationJob = async (jobId: string): Promise<ArtworkOptimizationJob> => {
  const response = await axiosInstance.get(`/artworks/workspace/optimization-jobs/${jobId}`);
  return response.data.data as ArtworkOptimizationJob;
};

export const cancelArtworkOptimizationJob = async (jobId: string): Promise<ArtworkOptimizationJob> => {
  const response = await axiosInstance.post(
    `/artworks/workspace/optimization-jobs/${jobId}/cancel`,
    {}
  );
  return response.data.data.job as ArtworkOptimizationJob;
};

export const finalizeArtworkOptimizationJob = async (
  jobId: string,
  selection: ArtworkOptimizationSelection
): Promise<{ job: ArtworkOptimizationJob; artwork: Artwork }> => {
  const response = await axiosInstance.post(
    `/artworks/workspace/optimization-jobs/${jobId}/finalize`,
    { selection }
  );
  return response.data.data as { job: ArtworkOptimizationJob; artwork: Artwork };
};

export const createArtworkApi = async (artwork: ArtworkPayload): Promise<Artwork> => {
  if(!artwork) throw new Error('Artwork data is required')

  const formData = new FormData()

  // description is string[]; append all items as separate fields with same key
  artwork.description.forEach((desc) => {
    formData.append("description", desc);
  });
  formData.append("artType", artwork.physical ? "PHYSICAL": "DIGITAL");
  formData.append("artistName", artwork.artistName);
  formData.append("title", artwork.title);
  formData.append("height", artwork.height);
  formData.append("materials", artwork.materials);
  formData.append("dateofcreation", artwork.dateofcreation);
  formData.append('artworkType', artwork.artworkType);
  const tags = artwork.tags.map(t => t.value).join(",")
  formData.append('tags', tags);

  if (artwork.nft) {
    formData.append("data", artwork.nft);
  }

  if (artwork.attachments && artwork.attachments.length > 0) {
    artwork.attachments.forEach((file) => {
      formData.append(`attachments`, file);
    });
  }
  const response = await axiosInstance.post<any>(`/artworks`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data.artwork;  
};

export const editArtworkApi = async (artworkId: string, partialArtwork: ArtworkPayload) => {
  if (!partialArtwork) throw new Error('Artwork data is required');

  const formData = new FormData();

  // description is string[]; append all items as separate fields with same key
  if (Array.isArray(partialArtwork.description)) {
    partialArtwork.description.forEach((desc) => {
      formData.append("description", desc);
    });
  }

  formData.append("artType", partialArtwork.physical ? "PHYSICAL" : "DIGITAL");
  formData.append("artistName", partialArtwork.artistName);
  formData.append("title", partialArtwork.title);
  formData.append("height", partialArtwork.height);
  formData.append("materials", partialArtwork.materials);
  formData.append("dateofcreation", partialArtwork.dateofcreation);
  formData.append("artworkType", partialArtwork.artworkType);

  const tags = partialArtwork.tags.map((t) => t.value).join(",");
  formData.append("tags", tags);

  appendJsonIfPresent(
    formData,
    "source",
    partialArtwork.source?.europeanaMetadata
      ? { europeanaMetadata: partialArtwork.source.europeanaMetadata }
      : undefined
  );
  appendJsonIfPresent(formData, "edmProfile", buildEditableEdmProfilePatch(partialArtwork.edmProfile));

  // Optional new primary file
  if (partialArtwork.nft) {
    formData.append("data", partialArtwork.nft);
  }

  // Optional new attachments
  if (partialArtwork.attachments && partialArtwork.attachments.length > 0) {
    partialArtwork.attachments.forEach((file: any) => {
      if (file.size && file.size > 0) {
        formData.append("attachments", file);
      } else {
        formData.append("attachments", JSON.stringify(file));
      }
    });
  }

  const resp = await axiosInstance.put(`/artworks/edit/${artworkId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return resp.data;
}

export const getArtworksApi = async (): Promise<Artwork[]> => {
  try {
    const response = await axiosInstance.get<any>('/artworks/owner')
    return response.data?.artworks;
  }catch(error) {
    console.log('Error fetching artworks', error)
    throw error;
  }
}

export const deleteArtworkApi = async (artworkId: string): Promise<boolean> => {
  try {
    const response = await axiosInstance.delete(`/artwork/${artworkId}`)
    if(response.data.status) {
      return true;
    } 
    return false;
  } catch(error) {
    throw error;
  }
}
